import { fetchOrganizations } from 'admin/actions/organization';
import { fetchShops } from 'admin/actions/shops';
import { fetchTerminals } from 'admin/actions/terminals';
import { Button } from 'admin/components/atoms/Buttons';
import CheckBox from 'admin/components/atoms/CheckBox';
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import { formatUUID, toQueryParams } from 'admin/utils';
import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { AnyStyledComponent } from 'styled-components';
import type { TransactionsQueryParams } from '.';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { ErrorsWrapper } from '../../atoms/WithErrors';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

type Props = {
  onConfirm: (params: TransactionsQueryParams) => void;
  closeModal: () => void;
  filterParams: TransactionsQueryParams;
};

type Organization = Required<Awaited<ReturnType<typeof fetchOrganizations>>>['rows'][number];
type Shop = Awaited<ReturnType<typeof fetchShops>>[number];
type Terminal = Awaited<ReturnType<typeof fetchTerminals>>[number];

// because the dateRange input, we need to combine to and from
type FormFilterParams = Omit<TransactionsQueryParams, 'from' | 'to'> & {
  dateRange: ComponentProps<typeof DateTimeRange>['dateRange'] | null;
};

type Option<T> = {
  label?: string;
  value?: T;
};

export const INITIAL_FILTER_PARAMS: TransactionsQueryParams = {
  organization: null,
  shop: null,
  terminal: null,
  transactionTypes: null,
  topupMethodTypes: null,
  isModified: null,
  from: null,
  to: null,
  transactionId: '',
  customerId: '',
  customerName: '',
  description: '',
  excludeCashbackAndExpire: true,
};

const ModalWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 160px 1fr;
    grid-template-rows: auto;
    justify-items: left;
    margin: 5px;
    align-items: center;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${Labeled} ${ErrorsWrapper} {
    text-align: left;
    margin: 0;
    font-size: 12px;
  }
  ${Select as AnyStyledComponent} {
    width: 500px;
    margin: 6px 0;
  }
  ${TextBox} {
    width: 500px;
    margin: 6px 0;
  }
  ${DateTimeRange} {
    width: 500px;
    margin: 6px 0;
  }
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #fff;
    border-left: solid 1px #ececec;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px #fff;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const LabeledTransactionTypesWrapper = styled.div`
  margin: 10px 0;

  ${Labeled} > ${Label} {
    align-self: baseline;
  }
`;

const TransactionTypesWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Label} {
    margin-bottom: 0;
  }
`;

const TransactionType = styled.div`
  display: flex;
  gap: 0.5em;
`;

const TopupMethodTypesWrapper = styled.div`
  margin-left: 1em;
  display: flex;
  flex-direction: column;
`;

const TopupMethodType = styled.div`
  display: flex;
  gap: 0.5em;
`;

const TopupMethodLabel = styled(Label)<{ disabled?: boolean }>`
  ${({ theme, disabled = false }) => (disabled ? `color: ${theme.colors.gray[300]}` : '')}
`;

const toOrganizationOption = (o: Organization): Option<Organization> => ({
  label: o.name,
  value: o,
});

const toShopOption = (s: Shop): Option<Shop> => ({
  label: s.name,
  value: s,
});

const toTerminalOption = (t: Terminal): Option<Terminal> => ({
  label: `${t.name}(${formatUUID(t.id)})`,
  value: t,
});

const FilterForm = ({ onConfirm, closeModal, filterParams }: Props) => {
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const { handleSubmit, control, setValue, reset, watch } = useForm<FormFilterParams>();

  const isTopupSelected = watch('transactionTypes')?.includes('topup');
  const isTopupMethodSelected = (watch('topupMethodTypes') || []).length > 0;

  const resetFilterForm = useCallback(() => {
    const { excludeCashbackAndExpire, from, to, ...rest } = INITIAL_FILTER_PARAMS;
    reset({
      ...rest,
      dateRange: { from, to },
      excludeCashbackAndExpire: false,
    });
  }, [reset]);

  const [organizationOptions, setOrganizationOptions] = useState<Option<Organization>[]>([]);
  const [shopOptions, setShopOptions] = useState<Option<Shop>[]>([]);
  const [terminalOptions, setTerminalOptions] = useState<Option<Terminal>[]>([]);

  const [errors, setErrors] = useState<{ [key in keyof FormFilterParams]?: string }>({});

  const canReadPersonalInfo = user.isAllowedTo('read', 'personalInfo');

  const onFetchOrganizations = (moneyId: string) => {
    const query = toQueryParams({
      with_issuer_org: true,
      private_money_id: moneyId,
      per_page: 10000,
    });
    fetchOrganizations(query)
      .then((res) => {
        setErrors((prev) => ({ ...prev, organization: undefined }));
        setOrganizationOptions(res.rows?.map((o) => ({ label: o.name, value: o })) ?? []);
      })
      .catch(() => {
        setOrganizationOptions([]);
        setErrors({
          organization:
            '通信エラーが発生しました。時間をおいて、安定した通信環境で再度画面表示をお試し下さい。',
        });
      });
  };

  const onFetchShops = (params: Record<string, unknown>) => {
    const query = toQueryParams(params);
    fetchShops(`${query}`).then((shops) => {
      setShopOptions(shops.map((s) => ({ label: s.name, value: { id: s.id, name: s.name } })));
    });
  };

  const onFetchTerminals = (moneyId: string, shopId?: string) => {
    const params = {
      private_money_id: moneyId,
      ...(shopId ? { shop_id: shopId } : {}),
    };
    fetchTerminals(toQueryParams(params)).then((terminals) => {
      setTerminalOptions(
        terminals.map((t) => ({
          label: `${t.name}(${formatUUID(t.id)})`,
          value: { id: t.id, name: t.name },
        }))
      );
    });
  };

  useEffect(() => {
    if (canReadPersonalInfo === true && userPrivateMoney.type !== 'own') {
      onFetchOrganizations(userPrivateMoney.id);
    } else {
      onFetchShops({ privateMoneyId: userPrivateMoney.id });
    }
  }, [userPrivateMoney, canReadPersonalInfo, resetFilterForm]);

  useEffect(() => {
    // rehydrate shops and terminals only once on load if it is already selected
    if (filterParams.organization?.code) {
      onFetchShops({ organizationCode: filterParams.organization.code });
    }
    if (filterParams.shop?.id) {
      onFetchTerminals(userPrivateMoney.id, filterParams.shop.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (params: FormFilterParams) => {
    const { dateRange, transactionTypes, topupMethodTypes, ...paramRest } = params;

    onConfirm({
      ...paramRest,
      transactionTypes: transactionTypes?.length === 0 ? null : transactionTypes,
      topupMethodTypes: topupMethodTypes?.length === 0 ? null : topupMethodTypes,
      from: dateRange?.from ?? null,
      to: dateRange?.to ?? null,
    });
  };

  return (
    <ModalWrapper>
      <form style={{ margin: 10 }} onSubmit={handleSubmit(onSubmit)}>
        <SelectedPrivateMoneyDisplay wrapperStyles={{ marginLeft: 6 }} />
        {canReadPersonalInfo && userPrivateMoney.type !== 'own' && (
          <Controller
            name="organization"
            control={control}
            defaultValue={filterParams.organization}
            render={({ field: { value, onChange } }) => (
              <LabeledSelect
                labelText="組織名"
                options={organizationOptions}
                value={value ? toOrganizationOption(value) : null}
                errors={errors.organization ? [errors.organization] : []}
                isMulti={false}
                onChange={(v) => {
                  onChange(v?.value ?? null);
                  setValue('shop', null);
                  setValue('terminal', null);
                  if (!v?.value?.code) return;
                  onFetchShops({ organizationCode: v.value?.code });
                }}
                placeholder="組織を選択してください"
              />
            )}
          />
        )}
        <Controller
          name="shop"
          control={control}
          defaultValue={filterParams.shop}
          render={({ field: { value, onChange } }) => (
            <LabeledSelect
              labelText="店舗名"
              options={shopOptions}
              value={value ? toShopOption(value) : null}
              isMulti={false}
              onChange={(v) => {
                onChange(v?.value ?? null);
                setValue('terminal', null);
                if (!v?.value?.id) return;
                onFetchTerminals(userPrivateMoney.id, v.value.id);
              }}
              placeholder="店舗を選択してください"
            />
          )}
        />
        <Controller
          name="terminal"
          control={control}
          defaultValue={filterParams.terminal}
          render={({ field: { value, onChange } }) => (
            <LabeledSelect
              labelText="端末ID"
              options={terminalOptions}
              value={value ? toTerminalOption(value) : null}
              isMulti={false}
              onChange={(v) => onChange(v?.value ?? null)}
              placeholder="端末を選択してください"
            />
          )}
        />
        <LabeledTransactionTypesWrapper>
          <Labeled labelText="取引種別">
            <TransactionTypesWrapper>
              <Controller
                name="transactionTypes"
                control={control}
                defaultValue={filterParams.transactionTypes ?? []}
                render={({ field: { value, onChange } }) => {
                  const transactionTypes = value ?? [];

                  return (
                    <>
                      <TransactionType>
                        <CheckBox
                          checked={transactionTypes.includes('payment')}
                          setChecked={(checked) => {
                            const newValue = checked
                              ? [...transactionTypes, 'payment']
                              : transactionTypes.filter((item) => item !== 'payment');

                            onChange(newValue);
                          }}
                        />
                        <Label>支払い</Label>
                      </TransactionType>
                      <TransactionType>
                        <CheckBox
                          checked={transactionTypes.includes('topup')}
                          setChecked={(checked) => {
                            const newValue = checked
                              ? [...transactionTypes, 'topup']
                              : transactionTypes.filter((item) => item !== 'topup');

                            if (!checked) {
                              setValue('topupMethodTypes', []);
                            }

                            onChange(newValue);
                          }}
                        />
                        <Label>チャージ</Label>
                      </TransactionType>
                    </>
                  );
                }}
              />
              <TopupMethodTypesWrapper>
                <Controller
                  name="topupMethodTypes"
                  control={control}
                  defaultValue={filterParams.topupMethodTypes ?? []}
                  render={({ field: { value, onChange } }) => {
                    const topupMethodTypes = value ?? [];

                    return (
                      <>
                        <TopupMethodType>
                          <CheckBox
                            disabled={!isTopupSelected}
                            checked={topupMethodTypes.includes('creditcard')}
                            setChecked={(checked) => {
                              const newValue = checked
                                ? [...topupMethodTypes, 'creditcard']
                                : topupMethodTypes.filter((item) => item !== 'creditcard');

                              onChange(newValue);
                            }}
                          />
                          <TopupMethodLabel disabled={!isTopupSelected}>
                            クレジットカードチャージ
                          </TopupMethodLabel>
                        </TopupMethodType>
                        <TopupMethodType>
                          <CheckBox
                            disabled={!isTopupSelected}
                            checked={topupMethodTypes.includes('bankpay')}
                            setChecked={(checked) => {
                              const newValue = checked
                                ? [...topupMethodTypes, 'bankpay']
                                : topupMethodTypes.filter((item) => item !== 'bankpay');

                              onChange(newValue);
                            }}
                          />
                          <TopupMethodLabel disabled={!isTopupSelected}>
                            Bank Payチャージ
                          </TopupMethodLabel>
                        </TopupMethodType>
                        <TopupMethodType>
                          <CheckBox
                            disabled={!isTopupSelected}
                            checked={topupMethodTypes.includes('sevenbank-atm')}
                            setChecked={(checked) => {
                              const newValue = checked
                                ? [...topupMethodTypes, 'sevenbank-atm']
                                : topupMethodTypes.filter((item) => item !== 'sevenbank-atm');

                              onChange(newValue);
                            }}
                          />
                          <TopupMethodLabel disabled={!isTopupSelected}>
                            セブン銀行ATMチャージ
                          </TopupMethodLabel>
                        </TopupMethodType>
                        <TopupMethodType>
                          <CheckBox
                            disabled={!isTopupSelected}
                            checked={topupMethodTypes.includes('cvs')}
                            setChecked={(checked) => {
                              const newValue = checked
                                ? [...topupMethodTypes, 'cvs']
                                : topupMethodTypes.filter((item) => item !== 'cvs');

                              onChange(newValue);
                            }}
                          />
                          <TopupMethodLabel disabled={!isTopupSelected}>
                            コンビニ決済
                          </TopupMethodLabel>
                        </TopupMethodType>
                      </>
                    );
                  }}
                />
              </TopupMethodTypesWrapper>
              <Controller
                name="excludeCashbackAndExpire"
                control={control}
                defaultValue={filterParams.excludeCashbackAndExpire}
                render={({ field: { value, onChange } }) => (
                  <TransactionType>
                    <CheckBox
                      checked={value ?? false}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                    <Label>失効・返金を除く</Label>
                  </TransactionType>
                )}
              />
              <Controller
                name="isModified"
                control={control}
                defaultValue={filterParams.isModified}
                render={({ field: { value, onChange } }) => (
                  <TransactionType>
                    <CheckBox
                      checked={value ?? false}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                    <Label>キャンセル済のみ</Label>
                  </TransactionType>
                )}
              />
            </TransactionTypesWrapper>
          </Labeled>
        </LabeledTransactionTypesWrapper>
        <Labeled labelText="抽出期間">
          <Controller
            name="dateRange"
            control={control}
            defaultValue={{
              from: filterParams.from,
              to: filterParams.to,
            }}
            render={({ field: { value, onChange } }) => (
              <DateTimeRange
                dateTimeProps={{
                  fromProps: { showIcon: true, wrapperStyles: { flex: 1 } },
                  toProps: { showIcon: true, wrapperStyles: { flex: 1 } },
                }}
                dateRange={value}
                setDateRange={onChange}
              />
            )}
          />
        </Labeled>
        <Labeled labelText="取引ID">
          <Controller
            name="transactionId"
            control={control}
            defaultValue={filterParams.transactionId || ''}
            render={({ field: { value, onChange } }) => (
              <TextBox syncValue={value} onChange={(v) => onChange(v.target.value)} />
            )}
          />
        </Labeled>
        <Labeled labelText="お客様ID">
          <Controller
            name="customerId"
            control={control}
            defaultValue={filterParams.customerId || ''}
            render={({ field: { value, onChange } }) => (
              <TextBox syncValue={value} onChange={(v) => onChange(v.target.value)} />
            )}
          />
        </Labeled>
        <Labeled labelText="お客様ニックネーム">
          <Controller
            name="customerName"
            control={control}
            defaultValue={filterParams.customerName || ''}
            render={({ field: { value, onChange } }) => (
              <TextBox syncValue={value} onChange={(v) => onChange(v.target.value)} />
            )}
          />
        </Labeled>
        <Labeled labelText="取引説明文">
          <Controller
            name="description"
            control={control}
            defaultValue={filterParams.description || ''}
            render={({ field: { value, onChange } }) => (
              <TextBox syncValue={value} onChange={(v) => onChange(v.target.value)} />
            )}
          />
        </Labeled>
        <FormActions>
          <Button onClick={closeModal} variant="neutral" type="button">
            キャンセル
          </Button>
          <Button type="button" onClick={() => resetFilterForm()} variant="neutral">
            クリア
          </Button>
          <Button type="submit">検索</Button>
        </FormActions>
      </form>
    </ModalWrapper>
  );
};

export default FilterForm;
